import React, { useCallback, useEffect, useState } from 'react';

import Grid from '@material-ui/core/Grid';
import ChevronRightRoundedIcon from '@material-ui/icons/ChevronRightRounded';
import { Formik } from 'formik';
import { ReactComponent as TrashIcon } from 'images/trashicon.svg';
import { forEach, isEmpty, isNil } from 'lodash';
import { trackerUpdate } from 'sdk';

import { dialogValidationSchema } from 'pages/Teachers/shared/AssignmentDialog/utils';
import { colors } from 'theme/palette';
import { setFormErrors } from 'utils/forms';
import { notifySuccess } from 'utils/notifications';

import Button from 'components/Button';
import DebouncedField from 'components/DebouncedField';
import EmojiInputField from 'components/EmojiInputField';
import InputField from 'components/InputField';
import Typography from 'components/Typography';

import styles from './styles.module.scss';

export const buildFormDataForUpdate = ({ values }) => {
  const formData = new FormData();
  formData.append('name', values.assignmentName);
  formData.append('description', values.description);
  forEach(values.attachments, (attachment) =>
    formData.append('attachment_file_ids', attachment.file.id)
  );

  return formData;
};

const EditAssignmentForm = ({ assignment, onClose }) => {
  const [initialValues, setInitialValues] = useState();

  useEffect(() => {
    if (!initialValues) {
      setInitialValues({
        assignmentName: assignment.name,
        description: assignment.description,
        tasks: assignment.tasks,
        attachments: assignment.attachments
      });
    }
  }, [assignment, initialValues]);

  const onSubmit = useCallback(
    async ({ values, setFieldError }) => {
      const formData = buildFormDataForUpdate({ values });

      const { success, errors } = await trackerUpdate(assignment.id, formData);

      if (success) {
        notifySuccess('Assignment succesfully updated.');
        onClose();
      } else {
        setFormErrors(errors, setFieldError);
      }
    },
    [assignment.id, onClose]
  );

  return initialValues ? (
    <div className={styles.container}>
      <Formik
        enableReinitialize
        validationSchema={dialogValidationSchema}
        initialValues={initialValues}
        onSubmit={(values, { setFieldError }) =>
          onSubmit({ values, setFieldError })
        }
      >
        {({
          errors,
          dirty,
          isSubmitting,
          handleSubmit,
          values,
          setFieldValue
        }) => {
          return (
            <>
              <Grid
                container
                alignItems="center"
                className={styles.actionButtons}
              >
                <Grid xs={4} item>
                  <Typography variant="H-TEXT-2" color={colors.blue1}>
                    Edit assignment
                  </Typography>
                </Grid>
                <Grid xs={8} item>
                  <Grid container spacing={2} justifyContent="flex-end">
                    <Grid item>
                      <Button
                        color="lightGrey"
                        disableElevation
                        onClick={onClose}
                        startIcon={<TrashIcon />}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        color="pink"
                        disabled={isSubmitting || !dirty || !isEmpty(errors)}
                        onClick={handleSubmit}
                        disableElevation
                        endIcon={<ChevronRightRoundedIcon />}
                      >
                        Update
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <DebouncedField
                value={values.assignmentName}
                component={EmojiInputField}
                hideEmojiTogglerOnInactiveInput
                autoFocus
                fullWidth
                onChange={(value) => setFieldValue('assignmentName', value)}
                error={!isNil(errors.assignmentName)}
                helperText={errors.assignmentName}
                type="text"
                placeholder="Assignment Name"
                variant="underlined"
                classes={{ root: styles.name }}
                EmojiButtonProps={{ tabIndex: '-1' }}
              />
              <DebouncedField
                value={values.description}
                component={InputField}
                multiline
                fullWidth
                minRows={1}
                onChange={(value) => setFieldValue('description', value)}
                error={!isNil(errors.description)}
                helperText={errors.description}
                type="text"
                placeholder="Optional Description..."
                variant="underlined"
                classes={{ root: styles.description }}
              />
            </>
          );
        }}
      </Formik>
    </div>
  ) : null;
};

export default EditAssignmentForm;
